import { FabricObject } from 'fabric';
import { Polygon } from 'polygon-clipping';
import React, { useMemo } from 'react';

import { MediaText, ElementAddress, Coords, DigitizedAssetText } from 'editor/src/store/design/types';
import { useSelector } from 'editor/src/store/hooks';
import shouldDigitizeCurrentSpread from 'editor/src/store/utils/shouldDigitizeCurrentSpread';

import useFabricUtils from 'editor/src/util/useFabricUtils';

import FabricImageComponent from 'editor/src/component/EditorArea/fabricComponents/FabricImageComponent';
import FabricTextInputComponent from 'editor/src/component/EditorArea/fabricComponents/FabricTextInputComponent';
import getElementRect from 'editor/src/component/EditorArea/Spread/Page/MediaElement/getElementRect';
import getTextClipPath from 'editor/src/component/EditorArea/Spread/Page/MediaElement/Text/getTextClipPath';
import getTextShadow from 'editor/src/component/EditorArea/Spread/Page/MediaElement/Text/getTextShadow';
import textElementDataToFabricProps from 'editor/src/component/EditorArea/Spread/Page/MediaElement/Text/textElementDataToFabricProps';
import { getFlexbileTextMaxWidth } from 'editor/src/component/EditorArea/Spread/Page/MediaElement/Text/updateTextElementWithoutRender';
import useDigitizedTextFabricOptionsUpdate from 'editor/src/component/EditorArea/Spread/Page/MediaElement/Text/useDigitizedTextFabricOptionsUpdate';
import useFontLoader from 'editor/src/component/EditorArea/Spread/Page/MediaElement/Text/useFontLoader';
import { NO_CANVAS_ROTATION } from 'editor/src/component/EditorArea/useCanvasRotation';

interface Props {
  element: MediaText;
  elementAddress: ElementAddress;
  pageCoords: Coords;
  contentClipPolygons: Polygon[];
  contentClipPath: FabricObject | undefined;
  areaWidth: number;
}

function TextElement({ element, elementAddress, pageCoords, contentClipPolygons, contentClipPath, areaWidth }: Props) {
  const { px2mm, mm2px } = useFabricUtils();

  const { loadedFontFamily, isFontLoaded } = useFontLoader(element.extra.fontFamily);
  const fabricProps = textElementDataToFabricProps(
    element,
    elementAddress.elementIndex,
    mm2px,
    false,
    false,
    loadedFontFamily,
  );
  const elementRect = useMemo(
    () => getElementRect(element, pageCoords, NO_CANVAS_ROTATION, mm2px),
    [element.x, element.y, element.r, element.width, element.height, pageCoords, mm2px],
  );

  const shadow = useMemo(
    () => getTextShadow(element.extra.shadow, element.extra.fontSize, mm2px),
    [element.extra.shadow, element.extra.fontSize, mm2px],
  );

  const clipPath = useMemo(
    () => getTextClipPath(elementRect, element.extra.fontSize, contentClipPolygons, contentClipPath),
    [element.extra.fontSize, elementRect, contentClipPolygons, contentClipPath],
  );

  const maxWidth = getFlexbileTextMaxWidth(
    elementRect.width,
    elementRect.left,
    element.extra.textAlign,
    pageCoords.left,
    areaWidth,
  );

  const digitizeCurrentSpread = useSelector(shouldDigitizeCurrentSpread);
  const digitizedAsset = useSelector<DigitizedAssetText | undefined>((state) =>
    element.digitizing_hash ? (state.design.digitizedAssets[element.digitizing_hash] as DigitizedAssetText) : undefined,
  );

  const showDigitizedAsset = useMemo(
    () => digitizeCurrentSpread && digitizedAsset?.url,
    [digitizeCurrentSpread, digitizedAsset?.url],
  );

  const getFabricOptionsOnUpdate = useDigitizedTextFabricOptionsUpdate(element, px2mm, digitizedAsset);

  return digitizedAsset?.url ? (
    <FabricImageComponent
      source={digitizedAsset.url}
      left={elementRect.left}
      top={elementRect.top}
      evented={false}
      selectable={false}
      zIndex={fabricProps.zIndex}
      clipPath={clipPath}
      opacity={element.hidden || !showDigitizedAsset ? 0 : 1}
      getFabricOptionsOnUpdate={getFabricOptionsOnUpdate}
      uuid={element.uuid}
      angle={element.r}
      crossOrigin="anonymous"
    />
  ) : (
    <FabricTextInputComponent
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...fabricProps}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...elementRect}
      isFontLoaded={isFontLoaded}
      clipPath={clipPath}
      borderOpacityWhenMoving={0.4}
      uuid={element.uuid}
      opacity={element.hidden || showDigitizedAsset ? 0 : 1}
      shadow={shadow || null}
      paintFirst="stroke"
      evented={false}
      selectable={false}
      maxWidth={maxWidth}
    />
  );
}

export default React.memo(TextElement);
